import { useState } from "react";
import type { ModalProps } from "@ui/modal";
import { useCodeBuilder } from "@/hooks/useCode/useCodeBuilder";
import { FormSchema } from "@/types/domain";
import { BaseCodeEditorModal } from "./base-code-editor-modal";

type ReferenceCodeEditorModalProps = Omit<ModalProps, "children"> & {
  title?: string;
  code?: string;
  minLines?: number;
  formSchema?: FormSchema;
  presentation?: "default" | "summary";
  placeholder?: string;
  onSave?: (value: string) => void;
};

export function ReferenceCodeEditorModal({
  title = "Expression",
  code: initCode,
  minLines = 18,
  formSchema,
  presentation = "default",
  placeholder = "getFormField('field_name').value",
  onSave,
  ...modalProps
}: ReferenceCodeEditorModalProps) {
  const [code, setCode] = useState(initCode ?? "");
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const { validateReference, validateSummaryExpression } = useCodeBuilder({
    formSchema,
  });

  const checkCode = async () => {
    const result =
      presentation === "summary"
        ? await validateSummaryExpression(code)
        : validateReference(code);
    if (!result.isValid) {
      setCodeError(result.errors[0]);
      return;
    }

    setCodeError(undefined);
    onSave?.(code);
    modalProps.close();
  };

  return (
    <BaseCodeEditorModal
      {...modalProps}
      title={title}
      code={code}
      onCodeChange={setCode}
      minLines={minLines}
      placeholder={placeholder}
      codeError={codeError}
      onCancel={modalProps.close}
      onConfirm={checkCode}
      maxWidthClassName={
        presentation === "summary" ? "max-w-[640px]" : undefined
      }
      presentation={presentation}
    />
  );
}
